import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthImage from "@/components/AuthImage";
import VendorPanel from "@/components/VendorPanel";
import VendorResponsesPanel from "@/components/order/VendorResponsesPanel";
import VendorFilesPanel from "@/components/order/VendorFilesPanel";
import EmailComposer from "@/components/EmailComposer";
import EmailLog from "@/components/EmailLog";
import MemberEmailComposer from "@/components/MemberEmailComposer";
import OrderSpecs from "@/components/order/OrderSpecs";
import OrderTimeline from "@/components/order/OrderTimeline";
import StatusControl from "@/components/order/StatusControl";
import MessagesThread from "@/components/order/MessagesThread";
import InternalNotesPanel from "@/components/order/InternalNotesPanel";
import ApprovalPanel from "@/components/orders/ApprovalPanel";
import LineItemsPanel from "@/components/orders/LineItemsPanel";
import VendorMessagesPanel from "@/components/orders/VendorMessagesPanel";
import DemandMeter from "@/components/DemandMeter";
import Gate from "@/components/Gate";
import { StatusStepper, StatusPill } from "@/components/Status";
import { ORDER } from "@/constants/testIds";

export default function OrderDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  if (error) return <div className="p-12 text-center text-[#8a1a1a]">{error}</div>;
  if (!order) return <div className="p-12 text-center overline">Loading…</div>;

  const isAdmin = user?.role === "club_admin" || user?.role === "golf_shop_staff";

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12">
        <Link to={isAdmin ? "/admin" : "/orders"} className="overline text-[#1A422D] hover:underline">
          ← Back
        </Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="overline">Order · {order.order_id}</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">
              {order.item_name}
            </h1>
            <div className="mt-2 text-[#4A4A4A]">
              {order.member_name} · {order.member_email}
            </div>
          </div>
          <div data-testid={ORDER.detailStatus}>
            <StatusPill status={order.status} />
          </div>
        </div>

        <div className="surface-card mt-8 p-8">
          <StatusStepper status={order.status} />
        </div>

        {/* iter60 — concierge origin banner + activity trail */}
        <ConciergeBanner order={order} isAdmin={isAdmin} onUpdated={setOrder} />

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <OrderSpecs order={order} />
          <aside className="space-y-6">
            {isAdmin && <StatusControl order={order} onUpdated={setOrder} />}
            <OrderTimeline history={order.status_history} />
          </aside>
        </div>

        {/* Vendor / fulfillment — pro-shop only.
            Members see only the status pill + stepper above; PO #, carrier,
            tracking, ETA, and vendor name are kept internal. */}
        {isAdmin && (
          <div className="mt-8 space-y-6">
            {/* iter67 §3 · Edit Item Details lives directly above the Demand
                Meter so staff can review specs before eyeing demand. */}
            <ItemDetailsPanel order={order} onUpdated={setOrder} />
            {/* iter67 §9/§12 · Independent line-item workflow — each item
                has its own status, vendor block, timeline, and packet. */}
            <LineItemsPanel order={order} onUpdated={setOrder} />
            <Gate cap="canViewDemandAnalytics">
              <DemandMeter orderId={order.order_id} />
            </Gate>
            <VendorPanel order={order} onUpdated={setOrder} />
            <VendorResponsesPanel order={order} />
            <VendorMessagesPanel order={order} />
            <VendorFilesPanel order={order} onUpdated={setOrder} />
            <EmailComposer order={order} onSent={setOrder} />
            <EmailLog order={order} />
          </div>
        )}

        {/* Member-side composer + email history. */}
        {!isAdmin && (
          <div className="mt-8 space-y-6">
            <MemberEmailComposer order={order} onSent={setOrder} />
            <EmailLog order={order} />
          </div>
        )}

        <MessagesThread order={order} currentUserRole={user?.role} onPosted={setOrder} />

        {isAdmin && (
          <>
            <LineItemPhotosPanel order={order} onUpdated={setOrder} />
            <VendorPacketOptionsPanel order={order} onUpdated={setOrder} />
            <ApprovalPanel order={order} onUpdated={setOrder} />
            <TimelineEditor order={order} onUpdated={setOrder} userRole={user?.role} />
          </>
        )}

        {isAdmin && <InternalNotesPanel order={order} onAdded={setOrder} />}
      </div>
    </div>
  );
}


// ============================================================================
// iter60 — Concierge Banner + Member Approve/Request-Changes + Activity History
// ============================================================================
function ConciergeBanner({ order, isAdmin, onUpdated }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);

  const isStaffCreated = order.origin && order.origin !== "member_created";
  const isAwaiting = order.status === "awaiting_member_approval";
  const isDraft = order.status === "draft" || order.status === "needs_research";

  const call = async (verb) => {
    setBusy(verb); setError("");
    try {
      const url = verb === "approve"
        ? `/orders/${order.order_id}/member-approve`
        : `/orders/${order.order_id}/member-request-changes`;
      const body = verb === "approve" ? { comment: comment || undefined } : { comment: comment.trim() };
      if (verb === "request_changes" && !body.comment) {
        setError("Please describe the changes you'd like."); setBusy(null); return;
      }
      const { data } = await api.post(url, body);
      onUpdated?.(data);
      setComment("");
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
    }
  };

  const addStaffNote = async () => {
    if (!note.trim()) return;
    setNoteBusy(true); setError("");
    try {
      await api.post(`/admin/orders/${order.order_id}/staff-note`, { staff_internal_note: note.trim() });
      const { data } = await api.get(`/orders/${order.order_id}`);
      onUpdated?.(data);
      setNote("");
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setNoteBusy(false);
    }
  };

  if (!isStaffCreated && !isAwaiting && !isDraft) return null;

  return (
    <div className="mt-8 space-y-4">
      {/* Origin banner */}
      {isStaffCreated && (
        <div
          data-testid="order-concierge-banner"
          className="p-5 bg-[#D9C68A]/15 border border-[#D9C68A] text-[#1A422D] flex items-start gap-4"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#8A6A2A]">
              {isAdmin ? "Origin" : "Concierge"}
            </div>
            <div className="mt-1 font-serif text-lg">
              {isAdmin
                ? `${order.origin === "quick_request" ? "Quick Request · " : "Staff Created · "}${order.origin_source || ""} · by ${order.created_by_staff_name || "staff"}`
                : "Created with assistance from your golf shop"}
            </div>
            {isAdmin && order.member_approval_status && (
              <div className="mt-1 text-xs text-[#4A4A4A]">
                Member approval status: <strong>{order.member_approval_status.replace(/_/g, " ")}</strong>
                {order.member_approval_comment && <> · &ldquo;{order.member_approval_comment}&rdquo;</>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Member approve / request-changes */}
      {!isAdmin && isAwaiting && (
        <div data-testid="member-approval-panel" className="p-6 bg-white border border-[#E5E3D8]">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">Awaiting your approval</div>
          <p className="mt-2 text-[#4A4A4A]">Your golf shop has prepared this request for you. Please review the details above.</p>
          <textarea
            data-testid="member-approval-comment"
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a note (optional for approval, required for change request)"
            className="mt-4 input-field"
          />
          {error && <div className="mt-3 text-sm text-[#8a1a1a]">{error}</div>}
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => call("approve")}
              disabled={busy}
              data-testid="member-approve-btn"
              className="px-6 py-3 bg-[#1A422D] text-[#F9F8F4] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] disabled:opacity-50"
            >
              {busy === "approve" ? "Approving…" : "Approve Request"}
            </button>
            <button
              type="button"
              onClick={() => call("request_changes")}
              disabled={busy}
              data-testid="member-request-changes-btn"
              className="px-6 py-3 border border-[#1A422D] text-[#1A422D] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] disabled:opacity-50"
            >
              Request Changes
            </button>
          </div>
        </div>
      )}

      {/* Admin — staff-only notes + activity log */}
      {isAdmin && (order.staff_notes?.length > 0 || order.activity_log?.length > 0) && (
        <div className="p-6 bg-white border border-[#E5E3D8]">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">Concierge Activity</div>
          {order.activity_log?.length > 0 && (
            <ul data-testid="order-activity-log" className="mt-4 space-y-2 text-sm">
              {order.activity_log.map((e, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#B59A5A]">·</span>
                  <span className="text-[#4A4A4A]">
                    <span className="text-[10px] uppercase tracking-widest text-[#7A7A7A] mr-2">
                      {new Date(e.at).toLocaleString()}
                    </span>
                    <strong className="text-[#1A422D]">{e.actor_name || "System"}</strong> · {(e.action || "").replace(/_/g, " ")}
                    {e.detail ? <> — {e.detail}</> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {order.staff_notes?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-[#F0EDE0]">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#8a1a1a]">Internal Notes · Not Visible to Member</div>
              <ul data-testid="order-staff-notes" className="mt-3 space-y-2 text-sm text-[#4A4A4A]">
                {order.staff_notes.map((n, i) => (
                  <li key={i} className="p-3 bg-[#fbe9e9]/40 border-l-2 border-[#a33]">
                    <div className="text-xs text-[#7A7A7A]">{n.by_name || n.by} · {new Date(n.at).toLocaleString()}</div>
                    <div className="mt-1 whitespace-pre-wrap">{n.note}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-5 flex gap-2">
            <input
              data-testid="order-staff-note-input"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add an internal note…"
              className="flex-1 input-field"
            />
            <button
              type="button"
              data-testid="order-staff-note-save"
              onClick={addStaffNote}
              disabled={noteBusy || !note.trim()}
              className="px-4 py-2 bg-[#1A422D] text-[#F9F8F4] text-[10px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] disabled:opacity-50"
            >
              {noteBusy ? "…" : "Add Note"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// iter61 §1+§2 — Edit Item Details panel
// ============================================================================
const EDITABLE_FIELDS = [
  ["item_name", "Item Name"], ["brand", "Brand"], ["vendor", "Vendor"],
  ["category", "Category"], ["subcategory", "Subcategory"],
  ["style_number", "Style Number"], ["sku", "SKU"],
  ["description", "Product Description", "textarea"],
  ["product_link", "Product Link"],
  ["size", "Size"], ["color", "Color"], ["quantity", "Quantity", "number"],
  ["gender", "Gender"], ["fit", "Fit"], ["material", "Material"], ["model", "Model"],
  ["logo_preference", "Club Logo"], ["logo_location", "Logo Placement"],
  ["logo_color", "Logo Color"],
  ["decoration_method", "Decoration Method"], ["thread_color", "Logo Thread Color"],
  ["monogram", "Monogram / Personalization"], ["decoration_notes", "Decoration Notes"],
  ["estimated_retail_price", "Estimated Retail", "number"],
  ["confirmed_member_price", "Confirmed Member Price", "number"],
  ["wholesale_price", "Wholesale (Staff Only)", "number"],
  ["estimated_lead_time", "Estimated Lead Time"],
  ["requested_delivery_date", "Requested Delivery Date"],
  ["vendor_notes", "Vendor Notes", "textarea"],
  ["notes", "Member Instructions (Visible to Member)", "textarea"],
];

function ItemDetailsPanel({ order, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({});

  useEffect(() => {
    const seed = {};
    EDITABLE_FIELDS.forEach(([k]) => { seed[k] = order?.[k] ?? ""; });
    setForm(seed);
  }, [order]);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const payload = {};
      EDITABLE_FIELDS.forEach(([k, , type]) => {
        const v = form[k];
        if (v === "" || v === null || v === undefined) return;
        payload[k] = type === "number" ? Number(v) : v;
      });
      const { data } = await api.patch(`/admin/orders/${order.order_id}/item-details`, payload);
      onUpdated?.(data);
      setMsg("Item details updated successfully.");
      setOpen(false);
    } catch (err) {
      setMsg(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8 p-6 bg-white border border-[#E5E3D8]" data-testid="item-details-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">Specifications</div>
          <p className="mt-1 text-sm text-[#7A7A7A]">Complete or update any product detail before creating the vendor packet.</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="edit-item-details-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A422D] text-[#F9F8F4] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C]"
        >
          Edit Item Details
        </button>
      </div>
      {msg && !open && <div data-testid="item-details-msg" className="mt-3 text-sm text-[#1A422D]">{msg}</div>}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="item-details-modal"
        >
          <div className="bg-white max-w-3xl w-full max-h-[92vh] overflow-y-auto p-8 border border-[#E5E3D8]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-serif text-2xl text-[#1A422D]">Edit Item Details</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-[#7A7A7A] text-2xl">×</button>
            </div>
            {msg && <div className="mt-3 px-3 py-2 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">{msg}</div>}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {EDITABLE_FIELDS.map(([k, label, type]) => (
                <div key={k} className={type === "textarea" ? "sm:col-span-2" : ""}>
                  <label className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A]">{label}</label>
                  {type === "textarea" ? (
                    <textarea
                      data-testid={`item-field-${k}`}
                      value={form[k] || ""}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                      rows={2}
                      className="mt-1 input-field"
                    />
                  ) : (
                    <input
                      data-testid={`item-field-${k}`}
                      type={type === "number" ? "number" : "text"}
                      value={form[k] ?? ""}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                      className="mt-1 input-field"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-[#E5E3D8] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A]">Cancel</button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="item-details-save"
                className="px-5 py-2 bg-[#1A422D] text-[#F9F8F4] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// iter66 — Reference Photos per line item (add/remove post-create)
// ============================================================================
const MAX_PHOTOS_PER_ITEM = 3;

function LineItemPhotosPanel({ order, onUpdated }) {
  const items = order.items || [];
  const [uploadingLineId, setUploadingLineId] = useState(null);
  const [duplicatingLineId, setDuplicatingLineId] = useState(null);
  const [error, setError] = useState("");
  const inputRefs = useRef({});

  if (!items.length) return null;

  const uploadForLine = async (lineId, fileList) => {
    if (!fileList || fileList.length === 0) return;
    setError("");
    setUploadingLineId(lineId);
    try {
      const newIds = [];
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newIds.push(data.photo_id);
      }
      const { data: updated } = await api.post(
        `/admin/orders/${order.order_id}/items/${lineId}/photos`,
        { photo_ids: newIds },
      );
      onUpdated?.(updated);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Upload failed.");
    } finally {
      setUploadingLineId(null);
      const ref = inputRefs.current[lineId];
      if (ref) ref.value = "";
    }
  };

  const removePhoto = async (lineId, photoId) => {
    setError("");
    try {
      const { data } = await api.delete(
        `/admin/orders/${order.order_id}/items/${lineId}/photos/${photoId}`,
      );
      onUpdated?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Remove failed.");
    }
  };

  // iter67 §10 · Duplicate a line item on an existing order.
  const duplicateLine = async (lineId, copyPhotos) => {
    setError("");
    setDuplicatingLineId(lineId);
    try {
      const { data } = await api.post(
        `/admin/orders/${order.order_id}/items/${lineId}/duplicate`,
        { copy_photos: copyPhotos },
      );
      onUpdated?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Duplicate failed.");
    } finally {
      setDuplicatingLineId(null);
    }
  };

  return (
    <div className="mt-6 p-6 bg-white border border-[#E5E3D8]" data-testid="line-item-photos-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">Reference Photos</div>
          <p className="mt-1 text-sm text-[#7A7A7A]">Attach up to {MAX_PHOTOS_PER_ITEM} photos per line item. Thumbnails appear inline on the vendor packet.</p>
        </div>
      </div>
      {error && <div className="mt-3 px-3 py-2 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm" data-testid="line-item-photos-error">{error}</div>}
      <div className="mt-5 space-y-5">
        {items.map((it, idx) => {
          const lineId = it.line_id;
          const photos = it.photos || [];
          const room = MAX_PHOTOS_PER_ITEM - photos.length;
          return (
            <div key={lineId || idx} className="border-t border-[#F0EDE0] pt-4 first:border-t-0 first:pt-0" data-testid={`line-photos-row-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-[#1A422D] font-serif">
                  Item {idx + 1} · <span className="text-[#1A1A1A]">{it.item_name || "(unnamed)"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => duplicateLine(lineId, window.confirm("Copy reference photos onto the new item?"))}
                    disabled={duplicatingLineId === lineId || !lineId}
                    data-testid={`line-duplicate-btn-${idx}`}
                    title="Duplicate this item"
                    className="inline-flex items-center gap-1 text-xs text-[#1A422D] hover:text-[#B59A5A] disabled:opacity-40"
                  >
                    {duplicatingLineId === lineId ? "Duplicating…" : "Duplicate item"}
                  </button>
                  <button
                    type="button"
                    onClick={() => inputRefs.current[lineId]?.click()}
                    disabled={room <= 0 || uploadingLineId === lineId || !lineId}
                    data-testid={`line-photos-upload-btn-${idx}`}
                    className="inline-flex items-center gap-1 text-xs text-[#1A422D] hover:text-[#B59A5A] disabled:opacity-40"
                  >
                    {uploadingLineId === lineId ? "Uploading…" : (room > 0 ? "Add photo" : "Max reached")}
                  </button>
                </div>
                <input
                  ref={(el) => { inputRefs.current[lineId] = el; }}
                  type="file"
                  accept="image/*,image/heic,image/heif"
                  multiple
                  hidden
                  data-testid={`line-photos-input-${idx}`}
                  onChange={(e) => uploadForLine(lineId, e.target.files)}
                />
              </div>
              {photos.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {photos.map((p) => (
                    <div key={p.photo_id} className="relative aspect-square border border-[#E5E3D8] bg-white overflow-hidden">
                      <AuthImage photoId={p.photo_id} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(lineId, p.photo_id)}
                        data-testid={`line-photos-remove-${idx}-${p.photo_id}`}
                        className="absolute top-1 right-1 bg-[#1A422D] text-white text-[9px] tracking-widest uppercase px-1.5 py-0.5"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-[#7A7A7A]">No photos attached to this item.</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}



// ============================================================================
// iter61 §4 — Include Member Notes on Vendor Packet toggle
// ============================================================================
function VendorPacketOptionsPanel({ order, onUpdated }) {
  const currentValue = order.include_member_notes_on_vendor_packet !== false; // default true
  const [saving, setSaving] = useState(false);
  const toggle = async (checked) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.order_id}/item-details`, {
        include_member_notes_on_vendor_packet: checked,
      });
      onUpdated?.(data);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-6 p-6 bg-white border border-[#E5E3D8]" data-testid="vendor-packet-options">
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">Vendor Packet</div>
      <label className="mt-4 flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid="include-member-notes-checkbox"
          checked={currentValue}
          onChange={(e) => toggle(e.target.checked)}
          disabled={saving}
          className="mt-1 w-4 h-4 accent-[#1A422D]"
        />
        <span className="text-sm text-[#4A4A4A]">
          <strong className="text-[#1A422D]">Include Member Notes on Vendor Packet</strong>
          <br />
          Internal Club Notes are never shared with the vendor regardless of this setting.
        </span>
      </label>
    </div>
  );
}

// ============================================================================
// iter61 §7+§8 — Timeline entry delete (soft-delete with required reason)
// ============================================================================
function TimelineEditor({ order, onUpdated, userRole }) {
  const [confirmIndex, setConfirmIndex] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const history = order.status_history || [];
  const isSA = userRole === "super_admin";

  const doDelete = async () => {
    if (!reason.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/admin/orders/${order.order_id}/timeline-entries/${confirmIndex}/delete`,
        { reason: reason.trim() },
      );
      onUpdated?.(data);
      setConfirmIndex(null); setReason("");
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (history.length === 0) return null;

  return (
    <div className="mt-6 p-6 bg-white border border-[#E5E3D8]" data-testid="timeline-editor">
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">Timeline</div>
      <ul className="mt-4 divide-y divide-[#F0EDE0]">
        {history.map((e, i) => {
          const isDeleted = e.deleted;
          const protectedEntry = ["closed", "cancelled", "ready"].includes(e.status);
          const canDelete = !isDeleted && (isSA || !protectedEntry);
          return (
            <li key={i} className={`py-3 flex items-start justify-between gap-4 ${isDeleted ? "opacity-50" : ""}`} data-testid={`timeline-entry-${i}`}>
              <div className="text-sm">
                <div className="text-[10px] uppercase tracking-widest text-[#7A7A7A]">
                  {e.at ? new Date(e.at).toLocaleString() : ""}
                </div>
                <div className="text-[#1A422D] mt-0.5">
                  {e.status || e.action || "Update"}
                  {e.note && <span className="text-[#4A4A4A]"> · {e.note}</span>}
                  {isDeleted && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#a33]">Deleted · {e.deletion_reason}</span>}
                </div>
              </div>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => { setConfirmIndex(i); setReason(""); }}
                  data-testid={`timeline-delete-${i}`}
                  className="text-[10px] uppercase tracking-widest text-[#a33] hover:underline"
                >
                  Delete
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {confirmIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setConfirmIndex(null)}
          data-testid="timeline-delete-modal"
        >
          <div className="bg-white max-w-md w-full p-8 border border-[#E5E3D8]" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-[#1A422D]">Delete Timeline Entry?</h3>
            <p className="mt-3 text-sm text-[#4A4A4A]">
              This will remove the selected timeline entry from the visible order history. This action should only be used to correct an accidental update.
            </p>
            <label className="mt-4 block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A]">
              Reason for Removal *
            </label>
            <select
              data-testid="timeline-delete-reason-preset"
              value={["Step selected accidentally","Incorrect status","Duplicate update","Wrong order"].includes(reason) ? reason : ""}
              onChange={(e) => setReason(e.target.value)}
              className="mt-2 input-field"
            >
              <option value="">— pick a common reason —</option>
              {["Step selected accidentally","Incorrect status","Duplicate update","Wrong order"].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <input
              data-testid="timeline-delete-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Or enter a custom reason"
              className="mt-2 input-field"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setConfirmIndex(null)} className="px-4 py-2 border border-[#E5E3D8] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A]">Cancel</button>
              <button
                type="button"
                onClick={doDelete}
                disabled={saving || !reason.trim()}
                data-testid="timeline-delete-confirm"
                className="px-5 py-2 bg-[#a33] text-white text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#8a1a1a] disabled:opacity-50"
              >
                {saving ? "Deleting…" : "Delete Entry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

